<template>
  <BaseModal
    :is-open="showModal"
    class="duplicate-modal"
    title="Duplicate session"
    description="Choose how you want to create a copy of this session."
    size="large"
    @close="showModal = false"
  >
    <div class="layout">
      <div class="column">
        <h2 class="secondary-heading">Prepare new PI</h2>
        <div class="text">
          Keep the configuration of the PI but update the teams, iteration names and iteration
          mapping.
        </div>
        <BaseButton
          :loading="loadingType === 'prepare'"
          :disabled="!!loadingType"
          @click="duplicate(false)"
        >
          Prepare new PI
        </BaseButton>
      </div>
      <div class="column">
        <h2 class="secondary-heading">Duplicate this PI</h2>
        <div class="text">
          Duplicate this PI but update the teams. Mostly used when the team composition changes
          before the PI Planning starts.
        </div>
        <BaseButton
          variant="outlined"
          :loading="loadingType === 'duplicate'"
          :disabled="!!loadingType"
          @click="duplicate(true)"
        >
          Duplicate
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { mapState } from "pinia";

import {
  safepiplanningsDuplicateModalOptionSelected,
  safepiplanningsDuplicateModalSeen,
} from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components-ng/BaseButton.vue";
import BaseModal from "@/components-ng/BaseModal.vue";
import { AlmConfigurationHandler } from "@/handlers/almConfigurationHandler";
import { SessionDuplicationService } from "@/handlers/sessionDuplicationService";
import { captureExceptionWithContext } from "@/sentry";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";
import { useToastStore } from "@/stores/toast";

export default {
  name: "DuplicateModal",
  components: { BaseButton, BaseModal },
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  data() {
    return {
      showModal: false,
      sessionId: "",
      /** Tracks which duplication is in progress (to show correct button states) */
      loadingType: null,
    };
  },
  computed: {
    ...mapState(useAppStore, ["company", "session"]),
    almConfigurationHandler() {
      return new AlmConfigurationHandler(createHandlerContext());
    },
  },
  methods: {
    showMessage(payload) {
      this.toastStore.showMessage(payload);
    },
    show(sessionId) {
      trackEvent(safepiplanningsDuplicateModalSeen());

      this.showModal = true;
      this.sessionId = sessionId;
    },
    async duplicate(withIterations) {
      trackEvent(safepiplanningsDuplicateModalOptionSelected(withIterations));

      this.loadingType = withIterations ? "duplicate" : "prepare";
      try {
        const sessionInfo = await this.almConfigurationHandler.getAlmConfiguration(this.sessionId);
        const almHandler = this.$almHandler(sessionInfo);
        await new SessionDuplicationService(
          this.sessionId,
          sessionInfo,
          almHandler,
          createHandlerContext(),
        ).duplicateSession(withIterations);
        const appStore = useAppStore();
        appStore.clearStateManager();
        this.$router.push({ name: "addnewsafepiplanning" });
      } catch (error) {
        captureExceptionWithContext(error);
        this.showMessage({
          message: `Failed loading the original PI Session data. Please validate that the original PI Session is properly displaying the data fetched from the ALM tool. Raw error: ${error.message}`,
          type: "error",
        });
      } finally {
        this.loadingType = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.secondary-heading {
  margin: 0 0 8px;
  font-size: 1rem;
  font-weight: 600;
}

.layout {
  display: flex;
  gap: 24px;

  .column {
    flex: 1;
    display: flex;
    flex-direction: column;

    button {
      width: 100%;
      margin-top: auto;
    }
  }

  .text {
    margin-bottom: 16px;
  }
}
</style>
